import { AlertCircle, Check, Clock, Eye, FileEdit, RotateCcw, XCircle, type LucideIcon } from 'lucide-react';

import type { TFunction } from 'i18next';

import type { ConsignmentStatusAction, ConsignmentTableData } from '@/entities/consignments';
import { getStatusActionLabels, type useConsignmentActions } from '@/features/consignment-actions';
import type { TableAction } from '@/shared/ui/data-table';

type ConsignmentActionHandlers = ReturnType<typeof useConsignmentActions>['actions'];

const STATUS_ACTIONS: ConsignmentStatusAction[] = ['ACCEPT', 'SET_ON_HOLD', 'CANCEL', 'RESET'];

const STATUS_ACTION_ICONS: Record<ConsignmentStatusAction, LucideIcon> = {
  ACCEPT: Check,
  SET_ON_HOLD: Clock,
  CANCEL: XCircle,
  RESET: RotateCcw
};

interface BuildConsignmentRowActionsArgs {
  actions: ConsignmentActionHandlers;
  t: TFunction;
}

/**
 * Builds the per-row action list for the consignments table.
 *
 * Each status action is shown only for rows whose `allowedActions` carry it: the backend has already applied the
 * transition map, the ADM/PRJ/PRA rule on cancelling, whether the consignment has lines, and site ownership.
 */
export function buildConsignmentRowActions({
  actions,
  t
}: BuildConsignmentRowActionsArgs): TableAction<ConsignmentTableData>[] {
  const statusLabels = getStatusActionLabels(t);

  return [
    {
      id: 'show-disposition',
      // listaction.RQ.DIS does not exist in any locale (raw key rendered);
      // cmrlist.showdisposition.label is the translated legacy JSF label.
      label: t('cmrlist.showdisposition.label', 'Show Disposition'),
      icon: Eye,
      onClick: (row) => actions.showDisposition([row.id])
    },
    {
      id: 'edit',
      label: t('listaction.RQ.EDT'),
      icon: FileEdit,
      intent: 'action',
      onClick: (row) => actions.editView([row.id])
    },
    {
      id: 'priority',
      label: t('listaction.RQ.PRT'),
      icon: AlertCircle,
      intent: 'action',
      onClick: (row) => actions.changePriority([row.id])
    },
    ...STATUS_ACTIONS.map(
      (action): TableAction<ConsignmentTableData> => ({
        id: action.toLowerCase().replace(/_/g, '-'),
        label: statusLabels[action],
        icon: STATUS_ACTION_ICONS[action],
        intent: action === 'CANCEL' ? 'danger' : 'action',
        isHidden: (row) => !row.allowedActions?.includes(action),
        onClick: (row) => actions.changeStatus(action, [row])
      })
    )
  ];
}
